/* eslint-disable react/no-unescaped-entities */
import { Helmet } from 'react-helmet'
import { Button, createStyles, Paper } from '@mantine/core'
import { Link } from 'react-router-dom'
import { Tile } from '../../components/tile'
import { Splash, SplashTitle } from '../../components/splash'
import img1 from '../../assets/large_mof_background.svg'

const useStyles = createStyles((theme) => ({
  h1: {
    fontSize: 36,
    fontWeight: 700,
    marginBottom: theme.spacing.md,
    marginTop: theme.spacing.xl,
    padding: '2em 8em',
    [theme.fn.smallerThan('lg')]: {
      padding: '1em 1.5em',
      [theme.fn.smallerThan('sm')]: {
        padding: theme.spacing.sm,
      },
    },
  },
  h2: {
    fontSize: 24,
    fontWeight: 600,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
  },
  list: {
    marginLeft: 24,
    marginBottom: theme.spacing.md,
  },
  quote: {
    fontStyle: 'italic',
    background: theme.colors.gray[9],
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    margin: '1.5em 0',
  },
  cta: {
    margin: '2em 0',
    display: 'flex',
    gap: theme.spacing.md,
    flexWrap: 'wrap',
  },
  splashText: {
    h1: {
      [theme.fn.smallerThan('sm')]: {
        fontSize: `${theme.fontSizes.lg * 2}px !important`,
      },
    },
    justifyContent: 'start',
    height: '100%',
  },
  text: {
    padding: '2em 8em',
    [theme.fn.smallerThan('lg')]: {
      padding: '1em 1.5em',
      [theme.fn.smallerThan('sm')]: {
        padding: theme.spacing.sm,
      },
    },
  },
}))

export const Calisthenics = (): JSX.Element => {
  const { classes } = useStyles()
  return (
    <main>
      <Helmet>
        <title>Expert Calisthenics Coaching in Coventry | Mistry of Fitness</title>
        <meta
          name='description'
          content='Structured calisthenics coaching in Coventry | Expert-led bodyweight training for muscle gain | Book your consultation now →'
        />
      </Helmet>
      <Splash height={'calc(100vh - 56px)'} backgroundSrc={`url(${img1})`}>
        <div className={classes.splashText}>
          <SplashTitle>Calisthenics Coaching in Coventry</SplashTitle>
        </div>
      </Splash>
      <div className='about-tile'>
        <Tile tileOpts={{ colour: 'bg-black', accent: 'bg-black', style: 'simple-3' }} mh={200}>
          <div className={classes.text}>
            <p>
              Unlock Your Strength Potential with Structured Bodyweight Training, Tailored for
              Skinny to Strong Transformations.
            </p>
            <p>
              At Mistry of Fitness, I specialise in helping you transition from skinny to strong
              using my transformative bodyweight training approach. Ideal for beginners, my programs
              are designed by a strength coach in Coventry to build functional fitness and lean
              muscle through proven, progressive methods.
            </p>
            <div className={classes.cta}>
              <Button component={Link} to='/contact-faqs' color='brand'>
                Contact Us
              </Button>
            </div>
          </div>
        </Tile>
        <Tile
          tileOpts={{ colour: 'bg-primary', accent: 'brand-primary', style: 'simple-1' }}
          mh={200}
        >
          <div className={classes.text}>
            <h2>Why Choose Mistry of Fitness?</h2>
            <ul className={classes.list}>
              <li>Rated 5 stars by Coventry locals</li>
              <li>Bespoke calisthenics and strength programs for rapid muscle gain</li>
              <li>Perfect for beginners: zero experience required</li>
              <li>Run by experienced personal trainers</li>
              <li>Located In Simply Gym Coventry for convenient access</li>
              <li>Proven "Skinny to Strong" framework with real success stories</li>
              <li>Transparent pricing, no hidden fees</li>
              <li>1:1 and small group sessions available</li>
            </ul>
            <Paper className={classes.quote}>
              "Rik is always so friendly and helpful at the gym! He has a super personable approach
              and helps you feel comfortable and at ease, but he also showcases super
              professionalism and knowledge in the field. I recommend him as a PT 100%!"
              <br />– Claudia Valler
            </Paper>
          </div>
        </Tile>
        <Tile
          tileOpts={{ colour: 'brand-secondary', accent: 'bg-tertiary', style: 'simple-3' }}
          mh={200}
        >
          <div className={classes.text}>
            <h2>What is Calisthenics?</h2>
            <p>
              Calisthenics is a form of functional fitness that leverages your own body weight to
              develop strength, flexibility, and control. It’s ideal for anyone looking to gain
              muscle, especially for those who find traditional gyms overwhelming.
            </p>
            <ul className={classes.list}>
              <li>Low-impact, joint-friendly movements</li>
              <li>Minimal equipment requirements</li>
              <li>Core-focused strength training</li>
              <li>Holistic muscle development</li>
            </ul>
          </div>
        </Tile>
        <Tile
          tileOpts={{ colour: 'bg-tertiary', accent: 'brand-primary', style: 'simple-1' }}
          mh={200}
        >
          <div className={classes.text}>
            <h2>Who is Calisthenics For?</h2>
            <p>My calisthenics for beginners is tailored to:</p>
            <ul className={classes.list}>
              <li>Skinny individuals aiming for visible muscle gain</li>
              <li>Busy professionals needing flexible schedules</li>
              <li>Students seeking cost-effective strength training</li>
              <li>Gym-goers looking to switch from weights to functional training</li>
            </ul>
            <p>
              If you're looking to build lasting strength and confidence, my expert-led calisthenics
              coaching is the perfect place to start. Whether you're new to fitness or ready to
              level up, I provide tailored support that gets real results. Book your free
              consultation today and take the first step toward a stronger, more capable you.
            </p>
          </div>
        </Tile>
        <Tile
          tileOpts={{ colour: 'brand-tertiary', accent: 'bg-primary', style: 'simple-2' }}
          mh={200}
        >
          <div className={classes.text}>
            <h2>Your Transformation Starts Here</h2>
            <p>
              I combine the fundamentals of calisthenics with targeted muscle-gain strategies,
              guided by a qualified personal trainer for muscle gain.
            </p>
            <p>My program includes:</p>
            <ul className={classes.list}>
              <li>Comprehensive initial assessment</li>
              <li>Personalised strength training and progression tracking</li>
              <li>Weekly bodyweight circuits</li>
              <li>Nutritional guidance for weight gain</li>
              <li>Community support and accountability</li>
            </ul>
          </div>
        </Tile>
        <Tile tileOpts={{ colour: 'bg-primary', accent: 'bg-primary', style: 'simple-3' }} mh={200}>
          <div className={classes.text}>
            <h2>Why Calisthenics is Perfect for Skinny to Strong Goals</h2>
            <p>
              Many underweight clients struggle to bulk up through traditional means. My
              muscle-building program in Coventry focuses on compound, bodyweight-driven movements
              that activate multiple muscle groups and stimulate lean gains naturally.
            </p>
          </div>
        </Tile>
        <Tile
          tileOpts={{ colour: 'brand-primary', accent: 'bg-black', style: 'simple-1' }}
          mh={200}
        >
          <div className={classes.text}>
            <h2>FAQs</h2>
            <p>
              <b>What’s the difference between calisthenics and weight training?</b>
              <br />
              Calisthenics uses your own body weight, offering better joint mobility and core
              strength. Weight training often isolates muscles with machines.
            </p>
            <br />
            <p>
              <b>Can I build serious muscle with calisthenics?</b>
              <br />
              Absolutely. With progressive overload and structured training, calisthenics builds
              significant strength and hypertrophy.
            </p>
            <br />
            <p>
              <b>How long before I see results?</b>
              <br />
              Clients typically notice improved strength and visible changes within 6–8 weeks with
              consistent effort.
            </p>
            <br />
            <p>
              <b>Do I need to be fit to start?</b>
              <br />
              No, my calisthenics for beginners sessions is perfect for complete newcomers.
            </p>
          </div>
        </Tile>
        <Tile
          tileOpts={{ colour: 'brand-secondary', accent: 'brand-primary', style: 'simple-2' }}
          mh={200}
        >
          <div className={classes.text}>
            <h2>Ready to Build Muscle and Confidence?</h2>
            <p>
              Take the first step toward a stronger you. Whether you're just starting out or looking
              to break through plateaus, I’m here to help as your calisthenics coach in Coventry.
            </p>
            <br />
            <p>Book Your Free Consultation Today</p>
            <div className={classes.cta}>
              <Button component={Link} to='/contact-faqs' color='brand'>
                Send Us A Message
              </Button>
            </div>
          </div>
        </Tile>
      </div>
    </main>
  )
}
